import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import logo from '../assets/images/logo.png';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import ChangePasswordDialog from './ChangePasswordDialog';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const { refreshPermissions } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  if (authLoading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const mustChangePassword = await login(email, password);
      // Refresh permissions after successful login
      await refreshPermissions();
      if (mustChangePassword) {
        // Show password change dialog
        setShowChangePassword(true);
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Une erreur est survenue lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChangeSuccess = () => {
    showSuccess('Mot de passe changé avec succès');
    setShowChangePassword(false);
    // Navigate to dashboard after successful password change
    setTimeout(() => {
      navigate(from, { replace: true });
    }, 1000);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img 
              src={logo} 
              alt="M.A.F Agencement Logo" 
              style={{ height: '120px', width: 'auto' }}
            />
          </Box>
          <Typography component="h2" variant="h6" align="center" gutterBottom>
            Connexion
          </Typography>
          

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              variant="filled"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              variant="filled"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Mot de passe"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Se connecter'}
            </Button>
          </Box>
          

        </Paper>
      </Box>
      
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      <ChangePasswordDialog
        open={showChangePassword}
        onClose={() => {}} // Prevent closing - forced change
        onSuccess={handlePasswordChangeSuccess}
        isForced={true}
      />
    </Container>
  );
};

export default Login;